import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { CountrySelect } from '@/components/form';
import { maxAdultBirthDate } from '../format';
import { EmailInput, PhoneInput, isPhoneValid } from '../inputs';
import { validateEmail } from '../validation';
import { INDIVIDUAL_ROLES, type DraftIndividual, emptyIndividual } from './types';

const DATE_INPUT =
  'w-full rounded-lg border border-[#e0e0e0] bg-white px-4 py-2.5 text-sm text-[#333] outline-none transition-colors focus:border-[#6B0F1A] focus:ring-1 focus:ring-[#6B0F1A]';

const SELECT_INPUT =
  'w-full rounded-lg border border-[#e0e0e0] bg-white px-4 py-2.5 text-sm text-[#333] outline-none transition-colors focus:border-[#6B0F1A] focus:ring-1 focus:ring-[#6B0F1A]';

interface Props {
  value: DraftIndividual[];
  onChange: (next: DraftIndividual[]) => void;
  errors: Record<string, string>;
}

const FIELD_ROW = 'grid grid-cols-1 gap-4 md:grid-cols-2';
const LABEL = 'mb-1 block text-xs font-medium text-[#555]';
const SECTION_HEADING = 'text-[11px] font-bold uppercase tracking-wider text-[#999]';

export function IndividualsPanel({ value, onChange, errors }: Props) {
  const updateAt = (index: number, patch: Partial<DraftIndividual>) => {
    const next = [...value];
    next[index] = { ...next[index], ...patch };
    onChange(next);
  };

  const remove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const add = () => onChange([...value, emptyIndividual()]);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="text-sm text-[#555]">
          Declare every person with a significant role in the business (directors, UBOs,
          shareholders above 25%, legal representatives). Tick the box next to the ones who should
          be able to log in to the Montelux portal. At least one person must be flagged to submit.
        </div>
        <Button variant="outline" size="sm" onClick={add}>
          + Add individual
        </Button>
      </div>

      {errors.individuals && <p className="text-sm text-[#c62828]">{errors.individuals}</p>}
      {errors['individuals.ownershipPercent'] && (
        <p className="text-sm text-[#c62828]">{errors['individuals.ownershipPercent']}</p>
      )}
      {totalOwnership(value) > 100.0001 && (
        <p className="text-sm text-[#c62828]">
          Combined ownership ({totalOwnership(value).toFixed(2)}%) exceeds 100%. Adjust the declared
          shares before submitting.
        </p>
      )}

      {value.length === 0 && (
        <div className="rounded-lg border border-dashed border-[#e0e0e0] bg-[#fafafa] p-6 text-center text-sm text-[#999]">
          No individual declared yet. Add at least one.
        </div>
      )}

      {value.map((person, index) => (
        <div key={index} className="space-y-5 rounded-lg border border-[#e0e0e0] bg-white p-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-[#333]">
              {person.firstName || person.lastName
                ? `${person.firstName} ${person.lastName}`.trim()
                : `Individual #${index + 1}`}
            </h4>
            <Button variant="outline" size="sm" onClick={() => remove(index)}>
              Remove
            </Button>
          </div>

          {/* Identity */}
          <section className="space-y-3">
            <h5 className={SECTION_HEADING}>Identity</h5>
            <div className={FIELD_ROW}>
              <div>
                <label className={LABEL}>First name *</label>
                <Input
                  value={person.firstName}
                  onChange={(v) => updateAt(index, { firstName: v })}
                />
              </div>
              <div>
                <label className={LABEL}>Last name *</label>
                <Input value={person.lastName} onChange={(v) => updateAt(index, { lastName: v })} />
              </div>
            </div>
            <div className={FIELD_ROW}>
              <div>
                <label className={LABEL}>Email *</label>
                <EmailInput value={person.email} onChange={(v) => updateAt(index, { email: v })} />
              </div>
              <div>
                <label className={LABEL}>Role *</label>
                <select
                  value={person.roleId}
                  onChange={(e) => updateAt(index, { roleId: Number(e.target.value) })}
                  className={SELECT_INPUT}
                >
                  {INDIVIDUAL_ROLES.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className={FIELD_ROW}>
              <div>
                <label className={LABEL}>Gender</label>
                <select
                  value={person.gender ?? ''}
                  onChange={(e) =>
                    updateAt(index, {
                      gender: (e.target.value || undefined) as DraftIndividual['gender'],
                    })
                  }
                  className={SELECT_INPUT}
                >
                  <option value="">—</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                  <option value="O">Other</option>
                </select>
              </div>
              <div>
                <label className={LABEL}>Date of birth</label>
                <input
                  type="date"
                  value={person.dateOfBirth ?? ''}
                  onChange={(e) => updateAt(index, { dateOfBirth: e.target.value || undefined })}
                  max={maxAdultBirthDate()}
                  className={DATE_INPUT}
                />
              </div>
            </div>
          </section>

          {/* Citizenship & residence */}
          <section className="space-y-3">
            <h5 className={SECTION_HEADING}>Citizenship & residence</h5>
            <div className={FIELD_ROW}>
              <div>
                <label className={LABEL}>Nationality</label>
                <CountrySelect
                  value={person.nationalityCountryCode ?? ''}
                  onChange={(v) => updateAt(index, { nationalityCountryCode: v || undefined })}
                />
              </div>
              <div>
                <label className={LABEL}>Country of residence</label>
                <CountrySelect
                  value={person.residenceCountryCode ?? ''}
                  onChange={(v) => updateAt(index, { residenceCountryCode: v || undefined })}
                />
              </div>
            </div>
            <div className={FIELD_ROW}>
              <div>
                <label className={LABEL}>Tax ID in country of residence</label>
                <Input
                  value={person.residenceTin ?? ''}
                  onChange={(v) => updateAt(index, { residenceTin: v || undefined })}
                />
              </div>
              <div />
            </div>
          </section>

          {/* Address */}
          <section className="space-y-3">
            <h5 className={SECTION_HEADING}>Address</h5>
            <div>
              <label className={LABEL}>Line 1</label>
              <Input
                value={person.addressLine1 ?? ''}
                onChange={(v) => updateAt(index, { addressLine1: v || undefined })}
              />
            </div>
            <div>
              <label className={LABEL}>Line 2</label>
              <Input
                value={person.addressLine2 ?? ''}
                onChange={(v) => updateAt(index, { addressLine2: v || undefined })}
              />
            </div>
            <div className={FIELD_ROW}>
              <div>
                <label className={LABEL}>City</label>
                <Input
                  value={person.addressCity ?? ''}
                  onChange={(v) => updateAt(index, { addressCity: v || undefined })}
                />
              </div>
              <div>
                <label className={LABEL}>State / region</label>
                <Input
                  value={person.addressState ?? ''}
                  onChange={(v) => updateAt(index, { addressState: v || undefined })}
                />
              </div>
            </div>
            <div className={FIELD_ROW}>
              <div>
                <label className={LABEL}>ZIP</label>
                <Input
                  value={person.addressZip ?? ''}
                  onChange={(v) => updateAt(index, { addressZip: v || undefined })}
                />
              </div>
              <div>
                <label className={LABEL}>Country</label>
                <CountrySelect
                  value={person.addressCountryCode ?? ''}
                  onChange={(v) => updateAt(index, { addressCountryCode: v || undefined })}
                />
              </div>
            </div>
          </section>

          {/* Telephone & ownership */}
          <section className="space-y-3">
            <h5 className={SECTION_HEADING}>Telephone & ownership</h5>
            <div className={FIELD_ROW}>
              <div>
                <label className={LABEL}>Telephone</label>
                <PhoneInput
                  value={person.telephone ?? ''}
                  onChange={(v) => updateAt(index, { telephone: v || undefined })}
                />
              </div>
              <div>
                <label className={LABEL}>Ownership percent</label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={0.1}
                  value={person.ownershipPercent?.toString() ?? ''}
                  onChange={(v) => {
                    if (v === '') {
                      updateAt(index, { ownershipPercent: undefined });
                      return;
                    }
                    const n = Number(v);
                    updateAt(index, {
                      ownershipPercent: Number.isFinite(n)
                        ? Math.min(100, Math.max(0, n))
                        : undefined,
                    });
                  }}
                />
              </div>
            </div>
          </section>

          {/* Compliance */}
          <section className="space-y-3">
            <h5 className={SECTION_HEADING}>Compliance</h5>

            <label
              className="flex items-start gap-3 rounded-md bg-[#f7f7f7] p-3 text-sm text-[#333]"
              data-testid={`pep-toggle-${index}`}
            >
              <input
                type="checkbox"
                checked={Boolean(person.isPep)}
                onChange={(e) =>
                  updateAt(index, {
                    isPep: e.target.checked,
                    pepName: e.target.checked ? person.pepName : undefined,
                    pepRelationship: e.target.checked ? person.pepRelationship : undefined,
                  })
                }
                className="mt-0.5 h-4 w-4 rounded border-[#c0c0c0] text-[#6B0F1A] focus:ring-[#6B0F1A]"
              />
              <span>
                <span className="font-medium">Politically Exposed Person (PEP)</span>
                <span className="block text-xs text-[#777]">
                  Tick if this person holds, or has held in the last 12 months, a prominent public
                  position — or is a close relative or known associate of someone who does.
                </span>
              </span>
            </label>

            {person.isPep && (
              <div className="grid grid-cols-1 gap-4 rounded-md border border-[#e0e0e0] bg-white p-3 md:grid-cols-2">
                <div>
                  <label className={LABEL}>PEP name *</label>
                  <Input
                    value={person.pepName ?? ''}
                    onChange={(v) => updateAt(index, { pepName: v || undefined })}
                  />
                </div>
                <div>
                  <label className={LABEL}>PEP relationship *</label>
                  <Input
                    value={person.pepRelationship ?? ''}
                    onChange={(v) => updateAt(index, { pepRelationship: v || undefined })}
                  />
                </div>
              </div>
            )}

            <label
              className="flex items-start gap-3 rounded-md bg-[#f7f7f7] p-3 text-sm text-[#333]"
              data-testid={`us-toggle-${index}`}
            >
              <input
                type="checkbox"
                checked={Boolean(person.usResident)}
                onChange={(e) =>
                  updateAt(index, {
                    usResident: e.target.checked,
                    usTin: e.target.checked ? person.usTin : undefined,
                  })
                }
                className="mt-0.5 h-4 w-4 rounded border-[#c0c0c0] text-[#6B0F1A] focus:ring-[#6B0F1A]"
              />
              <span>
                <span className="font-medium">US resident for tax purposes</span>
                <span className="block text-xs text-[#777]">
                  Tick if this person is a US citizen, green-card holder, or otherwise a US person
                  for IRS reporting.
                </span>
              </span>
            </label>

            {person.usResident && (
              <div className="rounded-md border border-[#e0e0e0] bg-white p-3">
                <label className={LABEL}>US TIN *</label>
                <Input
                  value={person.usTin ?? ''}
                  onChange={(v) => updateAt(index, { usTin: v || undefined })}
                />
              </div>
            )}
          </section>

          {/* Portal access */}
          <section className="space-y-3">
            <h5 className={SECTION_HEADING}>Portal access</h5>
            <label className="flex items-start gap-3 rounded-md bg-[#f7f7f7] p-3 text-sm text-[#333]">
              <input
                type="checkbox"
                checked={person.canLogin}
                onChange={(e) => updateAt(index, { canLogin: e.target.checked })}
                className="mt-0.5 h-4 w-4 rounded border-[#c0c0c0] text-[#6B0F1A] focus:ring-[#6B0F1A]"
              />
              <span>
                <span className="font-medium">Can log in to the Montelux webapp</span>
                <span className="block text-xs text-[#777]">
                  Sends an invitation email after KYC approval.
                </span>
              </span>
            </label>

            {person.canLogin && (
              <div className="space-y-2 rounded-md border border-[#e0e0e0] bg-white p-3">
                <div className="text-xs font-medium text-[#555]">
                  Initial portal permissions
                  <span className="block text-[11px] font-normal text-[#999]">
                    Pick the rights granted on first login. They can be edited later from the client
                    detail page → Persons tab.
                  </span>
                </div>
                {[
                  {
                    key: 'canViewTransactions' as const,
                    label: 'View transactions and balances',
                  },
                  {
                    key: 'canCreateTransfer' as const,
                    label: 'Create transfers',
                  },
                  { key: 'canCreateFx' as const, label: 'Run FX conversions' },
                  {
                    key: 'canManageBeneficiary' as const,
                    label: 'Manage beneficiaries',
                  },
                ].map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 text-sm text-[#333]">
                    <input
                      type="checkbox"
                      checked={Boolean(person[key])}
                      onChange={(e) => updateAt(index, { [key]: e.target.checked })}
                      className="h-4 w-4 rounded border-[#c0c0c0] text-[#6B0F1A] focus:ring-[#6B0F1A]"
                    />
                    {label}
                  </label>
                ))}
              </div>
            )}
          </section>
        </div>
      ))}
    </div>
  );
}

/**
 * Sum of declared ownership percentages. Used both by the panel's live
 * UBO-sum warning and by isIndividualsPanelValid to prevent submission
 * once the total exceeds 100. Floats are summed with a small epsilon
 * tolerance so 33.33 + 33.33 + 33.34 doesn't trip 100.000000001.
 */
function totalOwnership(value: DraftIndividual[]): number {
  return value.reduce((acc, p) => {
    const n = Number(p.ownershipPercent);
    return Number.isFinite(n) ? acc + n : acc;
  }, 0);
}

/**
 * A declared dateOfBirth is acceptable iff it's empty (optional field) or
 * lands on/before the maxAdultBirthDate cutoff. The native <input type=date>
 * already pins `max={maxAdultBirthDate()}`, but draft restoration and paste
 * paths bypass that, so we re-check at the gate.
 */
function isAdultDob(dateOfBirth: string | undefined): boolean {
  if (!dateOfBirth) return true;
  const cutoff = maxAdultBirthDate();
  return dateOfBirth <= cutoff;
}

/**
 * Best-effort one-line hint describing why isIndividualsPanelValid would
 * return false. Surfaced next to the disabled Submit button so operators
 * know which gate they're stuck behind. Returns null when the panel is
 * already valid.
 */
export function describeIndividualsPanelGap(value: DraftIndividual[]): string | null {
  if (value.length === 0) return 'Add at least one individual.';
  if (!value.some((p) => p.canLogin)) {
    return 'At least one person must be flagged "Can log in".';
  }
  if (totalOwnership(value) > 100.0001) {
    return 'Combined ownership exceeds 100%.';
  }
  for (const p of value) {
    if (p.isPep && !(p.pepName?.trim() && p.pepRelationship?.trim())) {
      return 'PEP name and relationship are required when PEP is ticked.';
    }
    if (p.usResident && !p.usTin?.trim()) {
      return 'US TIN is required when "US resident for tax purposes" is ticked.';
    }
  }
  return null;
}

export function isIndividualsPanelValid(value: DraftIndividual[]): boolean {
  if (value.length === 0) return false;
  if (!value.some((p) => p.canLogin)) return false;
  if (totalOwnership(value) > 100.0001) return false;
  return value.every(
    (p) =>
      Boolean(p.firstName.trim() && p.lastName.trim()) &&
      validateEmail(p.email.trim()) &&
      isPhoneValid(p.telephone) &&
      isAdultDob(p.dateOfBirth) &&
      Number.isInteger(p.roleId) &&
      p.roleId >= 1 &&
      p.roleId <= 6 &&
      // PEP requires name + relationship when toggled.
      (!p.isPep || (Boolean(p.pepName?.trim()) && Boolean(p.pepRelationship?.trim()))) &&
      // US resident requires TIN when toggled.
      (!p.usResident || Boolean(p.usTin?.trim())),
  );
}
